/**
 * useTenantExportBranding.js
 * 
 * Centralised hook that extracts tenant branding data for file exports.
 * Returns logo URL, Meta Title, and watermark config from tenant_branding.
 * 
 * Usage:
 *   const { logoUrl, portalName, watermark } = useTenantExportBranding();
 * 
 * Only returns data when the user is authenticated in a tenant portal.
 * Returns empty/null defaults for platform-level (non-tenant) contexts.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { useTheme } from '../context/ThemeContext';
import { getLogo, getLogoLight, getLogoDark, getCollapsedLogo } from '../utils/branding';
import { getTenantBranding } from '../api';

/**
 * Hook to provide tenant branding configuration for exports and UI headers.
 * Resolves local hostnames for S3 assets and handles theme-aware logo selection.
 */
export function useTenantExportBranding() {
    const { user, branding, token } = useAuth();
    const themeCtx = useTheme();
    const theme = themeCtx?.theme;
    const [loading, setLoading] = useState(false);

    // Derived values for convenience
    /**
     * Resolves the primary logo URL based on theme and availability.
     * Matches the logic in PlatformLayout.jsx sidebar.
     */
    const resolveLogoUrl = useCallback((targetTheme) => {
        const mode = targetTheme || theme?.mode || 'light';
        
        // Priority 1: User profile theme settings (Direct override)
        if (user?.theme_settings?.logo_url) return user.theme_settings.logo_url;

        // Priority 2: Theme context logo
        if (theme?.logo_url) return theme.logo_url;
        
        // Priority 3: Tenant branding utility
        return mode === 'dark' ? getLogoDark(branding) : getLogoLight(branding);
    }, [branding, theme, user]);

    // Derived values for convenience
    const logoUrl = resolveLogoUrl();
    const portalName = user?.tenantName || 'Tenant';
    const watermark = branding?.watermark_config || {};

    return {
        branding,
        logoUrl,
        portalName,
        watermark,
        loading,
        resolveLogoUrl
    };
}

/**
 * Generates an HTML header block for PDF/print exports with tenant branding.
 * The logo is centered above the title per requirement.
 * 
 * @param {{ logoUrl: string|null, portalName: string|null }} branding
 * @param {string} title - The title of the report being exported
 * @param {string} meta - Metadata (e.g. date/user string)
 * @returns {string} HTML string
 */
export function buildBrandedHeaderHTML(branding, title, meta, headerConfig = null) {
    const logoUrl = branding.logoUrl;
    const portalName = branding.portalName;
    
    // Apply header config defaults if not provided
    const config = headerConfig || {
        align: 'center',
        layout: 'stack',
        showLogo: true,
        subtitle: '',
        showSubtitle: false,
        companyName: '',
        showCompanyName: false,
        paddingTop: 0,
        paddingBottom: 24,
        showDivider: true,
        dividerStyle: 'solid',
        dividerColor: '#e2e8f0',
    };

    const align = config.align || config.logoAlign || 'center';
    const isInline = config.layout === 'inline';
    const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
    const justifyContent = justifyMap[align] || 'center';

    const paddingTop = config.paddingTop ?? 0;
    const paddingBottom = config.paddingBottom ?? 24;
    const borderStyle = config.showDivider 
        ? `1px ${config.dividerStyle || 'solid'} ${config.dividerColor || '#e2e8f0'}`
        : 'none';

    let logoPart = '';
    if (config.showLogo) {
        logoPart = logoUrl
            ? `<img src="${logoUrl}" alt="" style="max-height:48px;max-width:240px;object-fit:contain;display:block;" />`
            : (portalName ? `<span style="font-size:16px;font-weight:700;color:#1a1a2e;">${portalName}</span>` : '');
    }

    let textPart = '';
    const name = config.showCompanyName ? (config.companyName || portalName || '') : '';
    const subtitle = (config.showSubtitle && config.subtitle) ? config.subtitle : '';

    if (name || subtitle) {
        textPart = `
            <div style="display:flex;flex-direction:column;align-items:${align === 'right' ? 'flex-end' : (align === 'left' ? 'flex-start' : 'center')};">
                ${name ? `<div style="font-size:14px;font-weight:700;color:#0f172a;line-height:1.2;">${name}</div>` : ''}
                ${subtitle ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px;">${subtitle}</div>` : ''}
            </div>`;
    }

    // Wrap logo for spacing if stacked
    const logoWrapped = logoPart ? `<div style="margin-bottom:${!isInline && textPart ? '12px' : '0'};">${logoPart}</div>` : '';

    let content = '';
    if (isInline) {
        content = `
            <div style="display:flex;align-items:center;justify-content:${justifyContent};gap:16px;flex-direction:${align === 'right' ? 'row-reverse' : 'row'};">
                ${logoPart}
                ${textPart}
            </div>`;
    } else {
        content = `
            <div style="display:flex;flex-direction:column;align-items:${justifyContent};justify-content:center;text-align:${align};">
                ${logoWrapped}
                ${textPart}
            </div>`;
    }

    // Legacy title/meta (always centered below)
    let legacyTitlePart = title ? `<div style="text-align:center;font-size:22px;font-weight:800;color:#0f172a;margin-top:16px;">${title}</div>` : '';
    let legacyMetaPart = meta ? `<div style="text-align:center;font-size:11px;color:#94a3b8;margin-top:4px;">${meta}</div>` : '';

    return `
    <div style="padding-top:${paddingTop}px;padding-bottom:${paddingBottom}px;margin-bottom:24px;width:100%;font-family:sans-serif;border-bottom:${borderStyle};">
      ${content}
      ${legacyTitlePart}
      ${legacyMetaPart}
    </div>`;
}

/**
 * Generates an HTML footer block for PDF/print exports.
 */
export function buildBrandedFooterHTML(watermark, portalName, footerConfig = null) {
    const config = footerConfig || {
        showFooter: true,
        showDate: true,
        showPoweredBy: true,
        showCompanyName: true,
        customText: '',
        paddingTop: 24,
        paddingBottom: 0,
        showDivider: true,
        dividerStyle: 'solid',
        dividerColor: '#e2e8f0',
        align: 'center',
    };

    if (!config.showFooter) return '';

    const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
    const justifyContent = alignMap[config.align] || 'center';
    const borderStyle = config.showDivider 
        ? `1px ${config.dividerStyle || 'solid'} ${config.dividerColor || '#e2e8f0'}`
        : 'none';

    const parts = [];
    if (config.showPoweredBy) {
        parts.push(`Generated by ${portalName || 'AIDAS'} - AI Insight`);
    }
    if (config.showDate) {
        const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        parts.push(now);
    }
    if (config.customText) {
        parts.push(config.customText);
    }

    const content = parts.join(' · ');

    return `
    <div style="margin-top:32px;padding-top:${config.paddingTop ?? 24}px;padding-bottom:${config.paddingBottom ?? 0}px;border-top:${borderStyle};display:flex;justify-content:${justifyContent};font-family:sans-serif;">
       <div style="font-size:10.5px;color:#cbd5e1;text-align:${config.align || 'center'};">
         ${content}
       </div>
    </div>`;
}

/**
 * Generates watermark overlay HTML for PDF exports.
 * Returns empty string if no watermark is configured.
 * 
 * @param {{ text: string, position: string, opacity: number }|null} watermark
 * @returns {string} HTML string for an absolute-positioned watermark overlay
 */
export function buildWatermarkOverlayHTML(watermark) {
  if (!watermark?.text) return '';

  const posMap = {
    'top-left':      'top:20px;left:20px;',
    'top-center':    'top:20px;left:50%;transform:translateX(-50%);',
    'top-right':     'top:20px;right:20px;',
    'center-left':   'top:50%;left:20px;transform:translateY(-50%);',
    'center':        'top:50%;left:50%;transform:translate(-50%,-50%);',
    'center-right':  'top:50%;right:20px;transform:translateY(-50%);',
    'bottom-left':   'bottom:20px;left:20px;',
    'bottom-center': 'bottom:20px;left:50%;transform:translateX(-50%);',
    'bottom-right':  'bottom:20px;right:20px;',
  };

  const isDiagonal = watermark.position?.startsWith('diagonal');
  const isTile = watermark.position === 'tile';

  if (isTile) {
    const tiles = Array(12).fill(`<span style="font-size:14px;font-weight:bold;color:rgba(150,150,150,${watermark.opacity});text-transform:uppercase;letter-spacing:0.1em;transform:rotate(-12deg);white-space:nowrap;">${watermark.text}</span>`).join('');
    return `<div style="position:absolute;inset:0;display:flex;flex-wrap:wrap;gap:2rem 3rem;padding:2rem;justify-content:center;align-items:center;pointer-events:none;z-index:10;">${tiles}</div>`;
  }

  if (isDiagonal) {
    const angle = watermark.position === 'diagonal-rt-lb' ? '-35deg' : '35deg';
    return `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:10;overflow:hidden;">
      <span style="font-size:4vw;font-weight:bold;color:rgba(150,150,150,${watermark.opacity});text-transform:uppercase;letter-spacing:0.1em;white-space:nowrap;transform:rotate(${angle});">${watermark.text}</span>
    </div>`;
  }

  const posCSS = posMap[watermark.position] || posMap['bottom-right'];
  return `<div style="position:absolute;${posCSS}pointer-events:none;z-index:10;font-size:16px;font-weight:bold;color:rgba(150,150,150,${watermark.opacity});text-transform:uppercase;letter-spacing:0.1em;">${watermark.text}</div>`;
}
